import { API_URL } from '../constants/config';
import { SESSION_KEY } from '../constants/storageKeys';

async function request(path, options = {}) {
  const sessionToken = localStorage.getItem(SESSION_KEY);
  const headers = { 'Content-Type': 'application/json' };
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }

  const res = await fetch(`${API_URL}${path}`, {
    headers,
    ...options,
  });
  if (!res.ok) {
    const text = await res.text();
    let payload = null;
    try {
      payload = JSON.parse(text);
    } catch {
      // Fall through to the raw response text.
    }
    const err = new Error(payload?.error || text || `Request failed: ${res.status}`);
    err.code = payload?.code;
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// In-flight request deduplication: prevents the 5s→20s retry from sending a
// second backend request when the first is still processing.
const inflight = new Map();

export async function scoreWord(word, adj1, adj2, date) {
  const key = `${word}:${adj1}:${adj2}:${date}`;

  // If an identical request is already in flight, wait for it
  if (inflight.has(key)) {
    return inflight.get(key);
  }

  const body = JSON.stringify({ word, adj1, adj2, date });
  const opts = { method: 'POST', body };

  const promise = (async () => {
    // First attempt with 5s timeout (abort only the fetch, not the concept)
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      const result = await request('/api/score', { ...opts, signal: controller.signal });
      clearTimeout(timeout);
      return result;
    } catch (e) {
      if (e.name !== 'AbortError') throw e;
    }

    // Retry with longer timeout — mark as slow for UX feedback
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const result = await request('/api/score', { ...opts, signal: controller.signal });
      clearTimeout(timeout);
      return { ...result, slow: true };
    } catch (e) {
      clearTimeout(timeout);
      throw e;
    }
  })();

  inflight.set(key, promise);
  try {
    return await promise;
  } finally {
    inflight.delete(key);
  }
}

export async function submitGame(date, cells, totalScore, username, retryCount, protestResult) {
  return request('/api/submit', {
    method: 'POST',
    body: JSON.stringify({ date, cells, totalScore, username, retryCount, protestResult }),
  });
}

export async function getLeaderboard(date) {
  return request(`/api/leaderboard?date=${date}`);
}

export async function fetchPuzzleStatus(date) {
  return request(`/api/puzzle-status?date=${date}`);
}

export async function submitProtest(date, cellIndex, word, adj1, adj2, currentScore, argument) {
  return request('/api/protest', {
    method: 'POST',
    body: JSON.stringify({ date, cellIndex, word, adj1, adj2, currentScore, argument }),
  });
}
